import React from 'react'

import DELETE_ICON from '@public/assets/action/icon_delete.svg'
import EDIT_ICON from '@public/assets/action/icon_edit.svg'
import MERGE_ICON from '@public/assets/action/icon_merge.svg'
import RELEASE_ICON from '@public/assets/action/icon_release.svg'
import TUTORIAL_VIDEO from '@public/assets/welcome-wizard/manage-entries-tutorial-video.mp4'
import { useWelcomeWizardVideo } from '../../hooks/useWelcomeWizardVideo'

type InlineIconProps = {
  alt: string
  src: string
}

const InlineIcon = ({ alt, src }: InlineIconProps) => (
  <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-[#d9d9d9] bg-white">
    <img alt={alt} className="h-3.5 w-3.5" src={src} />
  </span>
)

function ManageEntriesStep() {
  const videoRef = useWelcomeWizardVideo(TUTORIAL_VIDEO)

  return (
    <div className="max-w-[900px]">
      <section>
        <h3 className="text-lg font-semibold">Edit, Delete, and Release</h3>
        <ul className="mt-2 space-y-1 pl-6 text-base leading-[1.35] tracking-[0.5px] text-[#666]">
          <li className="list-disc pl-0.5">
            <div className="inline-flex flex-wrap items-center gap-x-2 gap-y-1">
              <span>
                <strong className="font-semibold">Edit:</strong> Hover over an
                editable field and click
              </span>
              <InlineIcon alt="Edit" src={EDIT_ICON} />
              <span>(e.g. Narrative, Hrs, Task Code, Activity Code)</span>
            </div>
          </li>
          <li className="list-none py-1">
            <div className="rounded-lg border border-[#d9d9d9] bg-[#f5f5f5] px-5 py-2 text-sm leading-[1.42] tracking-[0.25px]">
              <strong className="font-bold">Tips</strong>: Click the button in
              the Status column to edit the entire entry.
            </div>
          </li>
          <li className="list-disc pl-0.5">
            <div className="inline-flex flex-wrap items-center gap-x-2 gap-y-1">
              <span>
                <strong className="font-semibold">Delete:</strong> Select one or
                more entries, then click
              </span>
              <InlineIcon alt="Delete" src={DELETE_ICON} />
              <span>in the action bar.</span>
            </div>
          </li>
          <li className="list-disc pl-0.5">
            <div className="inline-flex flex-wrap items-center gap-x-2 gap-y-1">
              <strong className="font-semibold">Release:</strong>
              <span>Click</span>
              <span className="inline-flex h-6 shrink-0 items-center gap-1 rounded-full border border-[#d9d9d9] bg-white px-2 text-xs leading-none tracking-normal text-[#333]">
                <img alt="" className="h-3.5 w-3.5" src={RELEASE_ICON} />
                Release All
              </span>
              <span>
                to release all unreleased entries, or select specific entries to
                release only those.
              </span>
            </div>
          </li>
        </ul>
      </section>

      <section className="mt-5">
        <h3 className="text-lg font-semibold">Merge</h3>
        <ul className="mt-2 space-y-1 pl-6 text-base leading-[1.35] tracking-[0.5px] text-[#666]">
          <li className="list-disc pl-0.5">
            <div className="inline-flex flex-wrap items-center gap-x-2 gap-y-1">
              <span>
                <strong className="font-semibold">
                  Merge all entries for a matter:
                </strong>{' '}
                Click
              </span>
              <InlineIcon alt="Merge" src={MERGE_ICON} />
              <span>in the top-right corner of the colored matter group.</span>
            </div>
          </li>
          <li className="list-disc pl-0.5">
            <div className="inline-flex flex-wrap items-center gap-x-2 gap-y-1">
              <span>
                <strong className="font-semibold">
                  Merge specific entries:
                </strong>{' '}
                Select two or more unreleased entries from the same matter, then
                click
              </span>
              <InlineIcon alt="Merge" src={MERGE_ICON} />
            </div>
          </li>
        </ul>
      </section>

      <video
        ref={videoRef}
        className="mt-5 aspect-[900/518] w-full rounded-[20px] bg-black object-contain shadow-[0_1px_2px_rgba(0,0,0,0.3),0_2px_6px_2px_rgba(0,0,0,0.15)]"
        controls
        autoPlay
        key={TUTORIAL_VIDEO}
        preload="metadata"
      >
        <source src={TUTORIAL_VIDEO} type="video/mp4" />
      </video>
    </div>
  )
}

export default ManageEntriesStep
